// PRD §3.3 — User profile + ECDSA public-key registration schemas.
import { z } from 'zod';
import { USER_ROLES } from './auth.js';
import { EntityTypeSchema, VetLicenseSchema } from './role.js';
export const UpdateProfileRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    dni: z.string().min(1).max(20).optional(),
    phone: z.string().min(5).max(20).optional(),
    // Phase 25 iter-15 — same format validation as UserRoleAssignment.vet_license
    // (3-12 chars alphanumeric, must contain ≥3 digits). Rejects obvious
    // garbage like "asdfasdf" before reaching the admin queue.
    vet_license: VetLicenseSchema.optional(),
    // Phase 12 (M3) — contract identity (REDESIGN §9.2). Collected during
    // setup (FarmSetup for FARMER; the BUYER 1st-purchase flow for BUYER).
    // When `entity_type` is set to PERSOA_XURIDICA in the same payload,
    // `legal_name` must also be present.
    entity_type: EntityTypeSchema.optional(),
    legal_name: z.string().min(1).max(160).optional(),
    fiscal_address: z.string().min(1).max(240).optional(),
    // Lote V-7 Fase B — datos fiscais completos para contrato compraventa.
    // NIF/CIF: 8 díxitos + letra (PF) ou letra + 7 díxitos + letra (PJ).
    // O regex acepta ambos formatos sen ser estrito de máis (a validación
    // real do díxito de control queda en cliente para evitar lista de
    // letras inválidas no schema).
    nif: z
        .string()
        .regex(/^[A-HJNP-SUVW0-9][0-9]{7}[A-Z0-9]$/, 'NIF/CIF formato inválido')
        .optional(),
    tax_address_line1: z.string().min(1).max(160).optional(),
    tax_address_city: z.string().min(1).max(80).optional(),
    tax_address_postal: z
        .string()
        .regex(/^\d{5}$/, 'Código postal de 5 díxitos')
        .optional(),
    tax_address_province: z.string().min(1).max(60).optional(),
    legal_representative_name: z.string().min(1).max(160).optional(),
    legal_representative_nif: z
        .string()
        .regex(/^[A-HJNP-SUVW0-9][0-9]{7}[A-Z0-9]$/, 'NIF formato inválido')
        .optional(),
    legal_representative_role: z.string().min(1).max(80).optional(),
})
    .strict()
    .refine((v) => v.entity_type !== 'PERSOA_XURIDICA' || !!v.legal_name, {
    message: 'legal_name is required when entity_type is PERSOA_XURIDICA',
    path: ['legal_name'],
});
export const PublicKeyRequestSchema = z
    .object({
    // ECDSA P-256 SPKI DER, PEM-encoded. Server verifies it parses; full
    // signature flow lives in events.ts.
    public_key: z.string().min(64).max(2048),
})
    .strict();
export const ProfileResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email().nullable(),
    phone: z.string().nullable(),
    name: z.string(),
    dni: z.string().nullable(),
    // Phase 12 (ADR-014, M2) — `roles` are the user's active role assignments
    // and `primary_role` is which one loads first. `role` is a DEPRECATED alias
    // of `primary_role`, kept until mobile migrates in M6.
    roles: z.array(z.enum(USER_ROLES)).min(1),
    primary_role: z.enum(USER_ROLES),
    role: z.enum(USER_ROLES),
    vet_license: z.string().nullable(),
    public_key: z.string().nullable(),
    farm_id: z.string().uuid().nullable(),
    // Phase 12 (M3) — contract identity (REDESIGN §9.2). `entity_type` has a
    // NOT NULL DEFAULT in Prisma so it's always present; `legal_name` and
    // `fiscal_address` may be null until the user fills them in setup.
    entity_type: EntityTypeSchema,
    legal_name: z.string().nullable(),
    fiscal_address: z.string().nullable(),
    // Lote V-7 Fase B — datos fiscais completos para contrato compraventa.
    nif: z.string().nullable(),
    tax_address_line1: z.string().nullable(),
    tax_address_city: z.string().nullable(),
    tax_address_postal: z.string().nullable(),
    tax_address_province: z.string().nullable(),
    legal_representative_name: z.string().nullable(),
    legal_representative_nif: z.string().nullable(),
    legal_representative_role: z.string().nullable(),
    // Phase 25 iter-15 — user avatar URL. Set via the avatar upload flow
    // (`POST /users/me/avatar/upload-url` → S3 PUT → `PATCH /users/me/avatar`).
    // null when the user has not chosen a photo (Initial-letter fallback).
    avatar_url: z.string().nullable(),
    created_at: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=user.js.map